/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    Component,
    ElementRef,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    Optional,
    QueryList,
    ViewChildren,
    ViewContainerRef,
} from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { Actions, Select, Store } from '@ngxs/store';
import { ActiveToolState } from './active-tool/state/active-tool.state';
import { MediaObserver } from '@angular/flex-layout';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { LoggerService } from '../../shared/services/logger.service';
import { HotKeyService } from '../../../core/ui/hotkeys/hot-key.service';
import { AuthService } from '../../../core/security/services/auth.service';
import { BottomSheetEnabled } from '../../../types/BottomSheetEnabled';
import { MapConfigState } from '../state/map-config.state';
import { MapToolWidgetConfiguration } from '../types/widget-configuration';

@Component({
    selector: 'dash-gis-map-toolbar',
    templateUrl: './map-toolbar.component.html',
    styleUrls: ['./map-toolbar.component.scss'],
})
export class MapToolbarComponent implements OnInit, OnDestroy, BottomSheetEnabled {
    public isBottomSheet: boolean;
    public bottomSheetColumnCount$: Observable<number>;

    @Select(ActiveToolState.activeTool) public activeWidget$: Observable<string>;
    public visibleWidgets$: Observable<MapToolWidgetConfiguration[]>;

    @Select(MapConfigState.loaded)
    private mapConfigLoaded$: Observable<boolean>;

    @Select(MapConfigState.toolbarWidgets)
    private toolbarWidgets$: Observable<MapToolWidgetConfiguration[]>;

    @Select(MapConfigState.bottomSheetWidgets)
    private bottomSheetWidgets$: Observable<MapToolWidgetConfiguration[]>;

    @ViewChildren('widgetTarget', { read: ViewContainerRef })
    private widgetContainers: QueryList<ViewContainerRef>;

    private disposeSubject$ = new Subject<void>();
    private shouldPreventBottomSheetClose = false;

    /**
     * @param elementRef {ElementRef}
     * @param logger {LoggerService}
     * @param authService {AuthService}
     * @param breakpointObserver {BreakpointObserver}
     * @param bottomSheet
     * @param data {any}
     */
    constructor(
        public elementRef: ElementRef,
        private logger: LoggerService,
        private authService: AuthService,
        private breakpointObserver: BreakpointObserver,
        @Optional() private bottomSheet: MatBottomSheetRef,
        @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) private data: any
    ) {
        this.isBottomSheet = false;
        if (this.data) {
            this.isBottomSheet = this.data.isBottomSheet || false;
        }

        this.visibleWidgets$ = this.getWidgets$();
        this.bottomSheetColumnCount$ = this.visibleWidgets$.pipe(
            map((visibleWidgets) => {
                if (this.breakpointObserver.isMatched(Breakpoints.XSmall)) {
                    return Math.min(5, visibleWidgets.length);
                }
                if (this.breakpointObserver.isMatched(Breakpoints.Small)) {
                    return Math.min(6, visibleWidgets.length);
                }
                if (this.breakpointObserver.isMatched(Breakpoints.Medium)) {
                    return Math.min(8, visibleWidgets.length);
                }
                if (this.breakpointObserver.isMatched(Breakpoints.Large)) {
                    return Math.min(12, visibleWidgets.length);
                }
            })
        );
    }

    ngOnInit() {}

    ngOnDestroy(): void {
        this.disposeSubject$.next();
    }

    @HostListener('click')
    clickListener() {
        if (this.isBottomSheet && this.bottomSheet && !this.shouldPreventBottomSheetClose) {
            this.bottomSheet.dismiss();
        }
    }

    handleShouldPreventClose(shouldPrevent: boolean) {
        this.shouldPreventBottomSheetClose = shouldPrevent;
    }

    getWidgets$(): Observable<MapToolWidgetConfiguration[]> {
        return this.mapConfigLoaded$.pipe(
            distinctUntilChanged(),
            filter((loaded) => loaded),
            switchMap(() => (this.isBottomSheet ? this.bottomSheetWidgets$ : this.toolbarWidgets$)),
            map((widgets) => widgets.filter((w) => this.authService.isAuthorized(w.roles || [])))
        );
    }
}
